import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Boxes, MousePointerClick, UserCheck, Gift, Store, Percent, FileText, Truck, CreditCard, RotateCcw, Receipt } from "lucide-react";
import { UNIVERSES } from "@/data/universes";
import { CtaButtons, ProductTile, useProProducts } from "@/components/home/shared";
import { pro15Active } from "@/lib/proOffer";

const WHY = [
  [Briefcase, "Un espace dédié aux professionnels", "Prix et commande réservés aux comptes professionnels validés."],
  [Boxes, "Une sélection adaptée à chaque métier", "Afro et cheveux texturés, salons mixtes, barbershops : des soins classés par univers."],
  [MousePointerClick, "Commander simplement", "Catalogue, panier et paiement en ligne, depuis un téléphone comme un ordinateur."],
  [UserCheck, "Un compte pour tout retrouver", "Vos informations, commandes, factures et devis au même endroit."],
  [Gift, "Des avantages réservés aux pros", pro15Active() ? "Remise personnelle, livraison offerte en métropole, réassort en un clic." : "Livraison offerte en métropole, devis et factures PDF, réassort en un clic."],
  [Store, "Pensé pour les salons et barbershops", "Commande au conditionnement, à utiliser au bac ou à revendre au comptoir."],
];

export const HomeWhy = () => (
  <section data-testid="home-why" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-24">
    <h2 className="font-serif text-3xl sm:text-4xl">Pourquoi choisir Ozentya ?</h2>
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-9">
      {WHY.map(([Icon, t, d]) => (
        <div key={t} className="flex gap-4">
          <div className="w-11 h-11 shrink-0 rounded-xl bg-[var(--oz-gold)]/15 flex items-center justify-center"><Icon className="w-5 h-5 text-[var(--oz-gold-hover)]" /></div>
          <div><h3 className="font-semibold">{t}</h3><p className="mt-1 text-sm text-[var(--oz-muted)] leading-relaxed">{d}</p></div>
        </div>
      ))}
    </div>
  </section>
);

export const PRO_ADVANTAGES = [
  [Percent, "Prix professionnels HT", "Prix unitaires hors taxes, TVA calculée automatiquement."],
  [Truck, "Livraison offerte en France métropolitaine", "Mondial Relay ou transporteur, livraison sous 10 à 15 jours, dès 300 € HT d'achat (sinon retrait au dépôt de Chatou). DOM-TOM : 30 à 60 € selon le montant. Belgique et Europe : 50 € HT."],
  [FileText, "Devis PDF en un clic", "Téléchargez un devis valable 15 jours, puis transformez-le en commande."],
  [Receipt, "Factures PDF", "Factures HT / TVA / TTC disponibles dans votre compte."],
  [CreditCard, "Paiement au choix", "Carte bancaire, PayPal ou virement bancaire."],
  [RotateCcw, "Réassort express", "Recommandez votre dernière commande en un clic."],
];

export const OfferHighlight = () => pro15Active() && (
  <div data-testid="pro-offer-15" className="rounded-3xl bg-[var(--oz-dark)] text-white p-7 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-6">
    <div className="font-serif text-6xl sm:text-7xl text-[var(--oz-gold)] leading-none">-15 %</div>
    <div className="flex-1">
      <p className="text-lg sm:text-xl font-semibold">Votre fidélité récompensée : profitez de votre remise professionnelle personnalisée de -15 % selon les conditions de l'offre Ozentya.</p>
      <p className="mt-3 text-sm text-white/65 leading-relaxed">Réservée aux clients professionnels éligibles. Votre code est personnel, lié à votre compte, utilisable uniquement sur Ozentya, sur les produits (hors livraison). Il s'affiche dans votre espace professionnel après connexion.</p>
    </div>
  </div>
);

export const HomeAdvantages = () => (
  <section data-testid="home-advantages" className="bg-[var(--oz-bg-2)]">
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-24">
      <h2 className="font-serif text-3xl sm:text-4xl">Les avantages de votre espace professionnel Ozentya</h2>
      {pro15Active() && <div className="mt-10"><OfferHighlight /></div>}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PRO_ADVANTAGES.map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl bg-white border border-[var(--oz-border)] p-5">
            <Icon className="w-5 h-5 text-[var(--oz-gold)]" /><h3 className="mt-3 font-semibold">{t}</h3>
            <p className="mt-1 text-sm text-[var(--oz-muted)] leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
      <Link to="/avantages-professionnels" data-testid="home-advantages-more" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold underline underline-offset-4">Tous les avantages et conditions d'accès <ArrowRight className="w-4 h-4" /></Link>
    </div>
  </section>
);

export const HomeProducts = () => {
  const products = useProProducts();
  if (!products?.length) return null;
  const fresh = products.filter((p) => p.slug === "serum-anti-taches-lumina-vg");
  const seen = new Set(fresh.map((p) => p.image));
  const selection = products.filter((p) => !fresh.includes(p) && !seen.has(p.image) && seen.add(p.image)).slice(0, fresh.length ? 7 : 8);
  return (
    <section data-testid="home-products" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl sm:text-4xl">Le catalogue professionnel</h2>
          <p className="mt-2 text-[var(--oz-muted)]">{products.length} références Ozentya, vendues par conditionnement.</p>
        </div>
        <Link to="/espace-pro" data-testid="home-products-all" className="inline-flex items-center gap-2 rounded-full border border-[var(--oz-text)] px-5 py-2.5 text-sm font-semibold hover:bg-[var(--oz-text)] hover:text-white transition-colors">Voir tout le catalogue <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {UNIVERSES.map((u) => <Link key={u.key} to={`/espace-pro?univers=${u.key}`} data-testid={`home-cat-${u.key}`} className="rounded-full bg-[var(--oz-bg-2)] px-4 py-2 text-sm hover:bg-[var(--oz-gold)]/20 transition-colors">{u.short}</Link>)}
      </div>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {fresh.map((p) => <div key={p.id} className="relative"><span className="absolute z-10 top-3 left-3 rounded-full bg-[var(--oz-badge)] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">Nouveauté</span><ProductTile p={p} /></div>)}
        {selection.map((p) => <ProductTile key={p.id} p={p} />)}
      </div>
    </section>
  );
};

export const HomeBrand = () => (
  <section id="marques" data-testid="home-brand" className="bg-[var(--oz-dark)] text-white">
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-20 grid md:grid-cols-[auto_1fr] gap-8 items-center">
      <div className="font-serif text-5xl">Ozentya<span className="text-[var(--oz-gold)]">.</span></div>
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)]">Notre marque</h2>
        <p className="mt-3 text-white/80 leading-relaxed max-w-2xl">Ozentya distribue sa propre marque de soins capillaires et barbe, née de 15 ans d'expérience autour des cheveux afro et texturés : pousse et cuir chevelu, hydratation, locks, barbe, visage.</p>
        <Link to="/a-propos" data-testid="home-brand-about" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--oz-gold)]">Notre histoire <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  </section>
);

export const HomeFinalCta = () => (
  <section data-testid="home-final-cta" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-24">
    <div className="rounded-3xl border-2 border-[var(--oz-gold)] p-8 sm:p-14 text-center">
      <h2 className="font-serif text-3xl sm:text-4xl max-w-3xl mx-auto leading-tight">Vous êtes coiffeur, barbier ou professionnel de la coiffure ? Rejoignez Ozentya.</h2>
      <p className="mt-5 text-[var(--oz-muted)] max-w-2xl mx-auto">Découvrez une plateforme pensée pour les professionnels de la coiffure afro, européenne et du barber shop. Créez votre compte et accédez à votre espace professionnel.</p>
      <div className="mt-8 flex justify-center"><CtaButtons testid="home-final" secondary={null} /></div>
      <p className="mt-5 text-sm">Déjà inscrit ? <Link to="/espace-pro/connexion" data-testid="home-final-login" className="font-semibold underline underline-offset-4">Se connecter</Link></p>
    </div>
  </section>
);
